import {Reducer} from 'redux';
import {persistReducer} from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import {LOGIN_SUCCESS, LOGOUT_SUCCESS, SET_CURRENT_USER} from './actions';
import {UserObjectApi} from '../../../utils/types';

export interface AuthState {
    isAuthenticated: boolean; // Indicates whether the user is authenticated
    user: UserObjectApi | null; // Stores the user's details or null if not logged in
}

const initialState: AuthState = {
    isAuthenticated: false,
    user: null,
};

/**
 * authReducer
 *
 * This reducer manages the authentication state of the application. It handles actions related
 * to user login and logout and updates the state accordingly.
 *
 * State Structure:
 * - `isAuthenticated`: A boolean indicating if the user is authenticated.
 * - `user`: The user object containing user details, or `null` if no user is logged in.
 *
 * Actions Handled:
 * - `LOGIN_SUCCESS`: Sets `isAuthenticated` to `true` and updates the `user` with the action's payload.
 * - `LOGOUT_SUCCESS`: Sets `isAuthenticated` to `false` and clears the `user` information.
 *
 * @param {AuthState} state - The current state of the authentication module.
 * @param {object} action - The dispatched action containing a `type` and optional `payload`.
 *
 * @returns {AuthState} The updated state based on the action type.
 */
const authReducer: Reducer<AuthState> = (state = initialState, action) => {
    switch (action.type) {
        case LOGIN_SUCCESS:
            if (typeof action.payload === 'object' && action.payload !== null) {
                return {...state, isAuthenticated: true, user: action.payload as UserObjectApi};
            }
            return state;
        case LOGOUT_SUCCESS:
            return {...state, isAuthenticated: false, user: null}; // Set isAuthenticated to false and clear the user
        case SET_CURRENT_USER:
            if (typeof action.payload === 'object' && action.payload !== null) {
                return { ...state, user: action.payload as UserObjectApi };
            }
            return state;
        default:
            return state;
    }
};

// Add token persistence configuration
const persistConfig = {
    key: 'auth', // Key for storing the authentication state
    storage, // Use localStorage for persistence
};

/**
 * persistedAuthReducer
 *
 * A persisted version of the `authReducer` that enables state persistence using `redux-persist`.
 * The state is saved to and rehydrated from `localStorage` under the key `auth`.
 *
 * @returns {Reducer<AuthState>} A reducer enhanced with persistence capabilities.
 */
const persistedAuthReducer = persistReducer(persistConfig, authReducer);

export default persistedAuthReducer;